import { useState, useCallback } from 'react';
import { AutoCompletionsData } from 'utils/helpers/chatbotHelpers';

/**
 * Custom hook for managing autocomplete functionality
 * @param autocompletions - The autocomplete data from the backend
 * @returns Object containing autocomplete state and handlers
 */
export const useAutocomplete = (autocompletions: AutoCompletionsData | null) => {
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [focusedIndex, setFocusedIndex] = useState(-1);

  const getFilteredSuggestions = useCallback((inputValue: string) => {
    if (!autocompletions) return [];
    return autocompletions.suggestions;
  }, [autocompletions]);

  const handleInputChange = useCallback((value: string) => {
    setShowSuggestions(value.length > 0);
    setFocusedIndex(-1);
  }, []);

  const handleKeyDown = useCallback(
    (
      e: React.KeyboardEvent,
      inputValue: string,
      onSendMessage: () => void,
      onSuggestionSelect: (suggestion: string) => void
    ) => {
      const filteredSuggestions = getFilteredSuggestions(inputValue);

      if (!showSuggestions || filteredSuggestions.length === 0) {
        if (e.key === 'Enter') {
          onSendMessage();
        }
        return;
      }

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setFocusedIndex(prev =>
            prev < filteredSuggestions.length - 1 ? prev + 1 : 0
          );
          break;
        case 'ArrowUp':
          e.preventDefault();
          setFocusedIndex(prev =>
            prev > 0 ? prev - 1 : filteredSuggestions.length - 1
          );
          break;
        case 'Enter':
          e.preventDefault();
          if (focusedIndex >= 0 && focusedIndex < filteredSuggestions.length) {
            const selectedSuggestion =
              filteredSuggestions[focusedIndex].QUESTIONS;
            onSuggestionSelect(selectedSuggestion);
            setShowSuggestions(false);
            setFocusedIndex(-1);
          } else {
            onSendMessage();
          }
          break;
        case 'Escape':
          e.preventDefault();
          setShowSuggestions(false);
          setFocusedIndex(-1);
          break;
        default:
          if (e.key === 'Enter') {
            onSendMessage();
          }
          break;
      }
    },
    [showSuggestions, focusedIndex, getFilteredSuggestions]
  );

  const handleSuggestionClick = useCallback(
    (suggestion: string, onSuggestionSelect: (suggestion: string) => void) => {
      onSuggestionSelect(suggestion);
      setShowSuggestions(false);
      setFocusedIndex(-1);
    },
    []
  );

  const resetAutocomplete = useCallback(() => {
    setShowSuggestions(false);
    setFocusedIndex(-1);
  }, []);

  return {
    showSuggestions,
    focusedIndex,
    getFilteredSuggestions,
    handleInputChange,
    handleKeyDown,
    handleSuggestionClick,
    resetAutocomplete
  };
};
